import {
  AlertTriangle,
  CalendarRange,
  Clock3,
  Download,
  ExternalLink,
  FileDown,
  FileSpreadsheet,
  ImageDown,
  Inbox,
  LoaderCircle,
  RefreshCw,
  Table2,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { ReportDataWarnings } from '../../components/ReportDataWarnings';
import {
  caseInflowWorkbookFileName,
  createCaseInflowWorkbook,
  type CaseInflowAnalysis,
} from '../../lib/case-inflow-analysis';
import { requireFirebaseFunctions } from '../../lib/firebase';
import { formatReportGeneration, OUTPUT_REPORT_CALLABLE_OPTIONS } from '../../lib/generated-report';
import {
  outputReportMonths,
  outputReportYears,
  type ProcessingAvailability,
} from '../../lib/processing-data';
import { svgElementMarkupForExport, svgMarkupDataUrl } from '../../lib/svg-export';

const MONTHS = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];
const INCOMING_COLOR = '#e93d71';
const CLOSED_COLOR = '#bfbfbf';

type LoadStatus = 'idle' | 'loading' | 'ready' | 'error';

function formatNumber(value: number): string {
  return new Intl.NumberFormat('it-IT').format(value);
}

function formatModifiedTime(value: string | null): string {
  if (!value) return 'Data non disponibile';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Data non disponibile';
  return new Intl.DateTimeFormat('it-IT', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

function errorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message.replace(/^Firebase:\s*/i, '').replace(/\s*\([^)]*\)\.?$/, '');
  }
  return 'Non è stato possibile generare il report Case in entrata.';
}

function defaultMonthForYear(year: number): number {
  const now = new Date();
  return year === now.getFullYear() ? Math.max(1, now.getMonth()) : 12;
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function niceAxisMaximum(maximum: number): { maximum: number; step: number } {
  if (maximum <= 0) return { maximum: 1000, step: 250 };
  const roughStep = maximum / 4;
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const normalized = roughStep / magnitude;
  const factor = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  const step = factor * magnitude;
  return { maximum: step * Math.ceil(maximum / step), step };
}

function demoAnalysis(): CaseInflowAnalysis {
  const values = [
    [2025, 8, 5791, 5740],
    [2025, 9, 8736, 8335], [2025, 10, 8847, 8865], [2025, 11, 8673, 9306], [2025, 12, 7592, 8263],
    [2026, 1, 7839, 7870], [2026, 2, 7280, 7545], [2026, 3, 6785, 7185], [2026, 4, 5892, 5742],
    [2026, 5, 6230, 6462], [2026, 6, 7348, 7223], [2026, 7, 7730, 7736], [2026, 8, 6447, 6257],
  ];
  const rows = values.map(([year, month, incoming, closed]) => ({
    year,
    month,
    label: `${MONTHS[month - 1].toLocaleLowerCase('it-IT')} ${year}`,
    incoming,
    closed,
    mailFallback: false,
    mailIncomplete: false,
    mailReferenceDate: '2026-09-21',
    components: {
      incoming: { currentMasterAllOpened: incoming, mailNonWebFirst12: 0, masterNonWebFirst12Fallback: 0, currentMasterNonWebFrom13: 0 },
      closed: { currentMasterClosedOpened: closed, mailNonWebFirst12: 0, masterNonWebFirst12Fallback: 0, currentMasterNonWebFrom13: 0, previousMastersClosed: 0 },
    },
  }));
  return {
    selectedYear: 2026,
    selectedMonth: 8,
    cutoffDay: 12,
    rows,
    totals: {
      incoming: rows.reduce((sum, row) => sum + row.incoming, 0),
      closed: rows.reduce((sum, row) => sum + row.closed, 0),
    },
    masterFiles: [2024, 2025, 2026].map((year) => ({
      id: `master-${year}`,
      name: `${year}.xlsx`,
      path: `Export / Export annuale / ${year}.xlsx`,
      modifiedTime: '2026-09-22T10:23:42.201Z',
      webViewLink: null,
      year,
      month: null,
    })),
    mailFiles: [{
      id: 'mail-2026',
      name: 'Report 21.09.2026.xlsx',
      path: 'Mail / 2026 / 09. Settembre / Report 21.09.2026.xlsx',
      modifiedTime: '2026-09-22T22:13:56.547Z',
      webViewLink: null,
      year: 2026,
      month: null,
      referenceDate: '2026-09-21',
    }],
    mailFallbackYears: [],
    mailFallbackPeriods: [],
    mailIncompletePeriods: [],
  };
}

const CaseInflowChart = forwardRef<SVGSVGElement, { analysis: CaseInflowAnalysis }>(function CaseInflowChart({ analysis }, ref) {
  const highestValue = Math.max(0, ...analysis.rows.flatMap((row) => [row.incoming, row.closed]));
  const axis = niceAxisMaximum(highestValue);
  const chart = { x: 74, y: 36, width: 1130, height: 310 };
  const periodWidth = chart.width / analysis.rows.length;
  const barWidth = Math.min(18, periodWidth * 0.3);
  const ticks = Array.from({ length: Math.round(axis.maximum / axis.step) + 1 }, (_, index) => index * axis.step);

  return (
    <svg ref={ref} className="trend-chart-svg case-inflow-chart-svg" viewBox="0 0 1280 535" role="img" aria-labelledby="case-inflow-chart-title case-inflow-chart-description" xmlns="http://www.w3.org/2000/svg">
      <title id="case-inflow-chart-title">Case in entrata e chiusi</title>
      <desc id="case-inflow-chart-description">Confronto mensile tra case in entrata e case chiusi nel mese selezionato e nei dodici mesi precedenti.</desc>
      <rect width="1280" height="535" rx="12" fill="#ffffff" />
      <text data-export-title="true" x="74" y="21" fill="#173249" fontFamily="Inter, Arial, sans-serif" fontSize="15" fontWeight="700">Andamento annuale</text>

      {ticks.map((tick) => {
        const y = chart.y + chart.height - (tick / axis.maximum) * chart.height;
        return (
          <g key={tick}>
            <line x1={chart.x} x2={chart.x + chart.width} y1={y} y2={y} stroke="#dfe4e7" strokeWidth="1" />
            <text x={chart.x - 13} y={y + 4} textAnchor="end" fill="#5f6f7b" fontFamily="Inter, Arial, sans-serif" fontSize="11">{formatNumber(tick)}</text>
          </g>
        );
      })}

      {analysis.rows.map((row, index) => {
        const centerX = chart.x + periodWidth * index + periodWidth / 2;
        const incomingHeight = (row.incoming / axis.maximum) * chart.height;
        const closedHeight = (row.closed / axis.maximum) * chart.height;
        return (
          <g key={`${row.year}-${row.month}`}>
            <rect className="trend-chart-bar" x={centerX - barWidth - 1} y={chart.y + chart.height - incomingHeight} width={barWidth} height={incomingHeight} rx="2" fill={INCOMING_COLOR}>
              <title>{row.label}: In entrata, {formatNumber(row.incoming)}</title>
            </rect>
            <rect className="trend-chart-bar" x={centerX + 1} y={chart.y + chart.height - closedHeight} width={barWidth} height={closedHeight} rx="2" fill={CLOSED_COLOR}>
              <title>{row.label}: Chiusi, {formatNumber(row.closed)}</title>
            </rect>
            <text transform={`rotate(-45 ${centerX + 4} ${chart.y + chart.height + 27})`} x={centerX + 4} y={chart.y + chart.height + 27} textAnchor="end" fill="#3f4d58" fontFamily="Inter, Arial, sans-serif" fontSize="10.5" fontWeight="600">{row.label}</text>
          </g>
        );
      })}
      <line x1={chart.x} x2={chart.x + chart.width} y1={chart.y + chart.height} y2={chart.y + chart.height} stroke="#687782" strokeWidth="1.2" />
      <g transform="translate(510 500)">
        <rect width="14" height="14" rx="3" fill={INCOMING_COLOR} />
        <text x="22" y="12" fill="#263c4d" fontFamily="Inter, Arial, sans-serif" fontSize="12" fontWeight="650">In entrata</text>
        <rect x="130" width="14" height="14" rx="3" fill={CLOSED_COLOR} />
        <text x="152" y="12" fill="#263c4d" fontFamily="Inter, Arial, sans-serif" fontSize="12" fontWeight="650">Chiusi</text>
      </g>
    </svg>
  );
});

export function CaseInflowPanel({ availability, demo }: { availability: ProcessingAvailability | null; demo: boolean }) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [year, setYear] = useState<number | ''>('');
  const [month, setMonth] = useState<number | ''>('');
  const [analysis, setAnalysis] = useState<CaseInflowAnalysis | null>(null);
  const [status, setStatus] = useState<LoadStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const availableYears = outputReportYears(availability, 'case-inflow');
  const selectedYear = year;
  const availableMonths = selectedYear
    ? outputReportMonths(availability, 'case-inflow', selectedYear)
    : [];
  const selectedMonth = month;

  useEffect(() => {
    if (year || availableYears.length === 0) return;
    const initialYear = availableYears[0];
    const timeout = window.setTimeout(() => {
      setYear(initialYear);
      setMonth(outputReportMonths(availability, 'case-inflow', initialYear).at(-1) ?? defaultMonthForYear(initialYear));
    }, 0);
    return () => window.clearTimeout(timeout);
  }, [availability, availableYears, year]);

  const loadAnalysis = useCallback(async (regenerate = false) => {
    if (!selectedYear || !selectedMonth) return;
    setStatus('loading');
    setError(null);
    setExportError(null);
    try {
      const result = demo
        ? {
            ...demoAnalysis(),
            generation: {
              generatedAt: new Date().toISOString(),
              source: regenerate ? 'manual' as const : 'nightly' as const,
              cached: !regenerate,
              version: 1,
            },
          }
        : (await httpsCallable<{ year: number; month: number; regenerate: boolean }, CaseInflowAnalysis>(
            requireFirebaseFunctions(),
            'getOutputCaseInflowReport',
            OUTPUT_REPORT_CALLABLE_OPTIONS,
          )({ year: selectedYear, month: selectedMonth, regenerate })).data;
      setAnalysis(result);
      setStatus('ready');
    } catch (cause) {
      setError(errorMessage(cause));
      setStatus('error');
    }
  }, [demo, selectedMonth, selectedYear]);

  useEffect(() => {
    if (!selectedYear || !selectedMonth || status !== 'idle') return;
    const timeout = window.setTimeout(() => void loadAnalysis(false), 0);
    return () => window.clearTimeout(timeout);
  }, [loadAnalysis, selectedMonth, selectedYear, status]);

  const exportSvg = () => {
    if (!svgRef.current || !analysis) return;
    const serialized = svgElementMarkupForExport(svgRef.current);
    downloadBlob(new Blob([`<?xml version="1.0" encoding="UTF-8"?>\n${serialized}`], { type: 'image/svg+xml;charset=utf-8' }), `case-in-entrata-${analysis.selectedYear}-${String(analysis.selectedMonth).padStart(2, '0')}.svg`);
  };

  const exportPng = async () => {
    if (!svgRef.current || !analysis) return;
    setExportError(null);
    try {
      const serialized = svgElementMarkupForExport(svgRef.current);
      const image = new Image();
      image.decoding = 'async';
      await new Promise<void>((resolve, reject) => {
        image.onload = () => resolve();
        image.onerror = () => reject(new Error('Immagine non generata'));
        image.src = svgMarkupDataUrl(serialized);
      });
      const canvas = document.createElement('canvas');
      canvas.width = 2560;
      canvas.height = 1070;
      const context = canvas.getContext('2d');
      if (!context) throw new Error('Canvas non disponibile');
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      const png = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png', 1));
      if (!png) throw new Error('PNG non generato');
      downloadBlob(png, `case-in-entrata-${analysis.selectedYear}-${String(analysis.selectedMonth).padStart(2, '0')}.png`);
    } catch {
      setExportError('Non è stato possibile esportare il grafico in PNG. Puoi usare il formato SVG.');
    }
  };

  const exportExcel = () => {
    if (!analysis) return;
    setExportError(null);
    try {
      const workbook = createCaseInflowWorkbook(analysis);
      downloadBlob(new Blob([Uint8Array.from(workbook).buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), caseInflowWorkbookFileName(analysis.selectedYear, analysis.selectedMonth));
    } catch {
      setExportError('Non è stato possibile creare il file Excel.');
    }
  };

  const requestedMaster = analysis?.masterFiles.find((file) => file.year === analysis.selectedYear);
  const latestMail = analysis?.mailFiles.find((file) => file.year === analysis.selectedYear);

  return (
    <>
      <section className="trend-period-card">
        <div className="trend-period-copy">
          <span><CalendarRange size={22} /></span>
          <div>
            <p>Periodo di analisi</p>
            <h2>Seleziona anno e mese</h2>
            <small>Il report mostra il mese selezionato e i dodici mesi precedenti.</small>
          </div>
        </div>
        <div className="trend-period-actions">
          <label>
            <span>Anno</span>
            <select value={selectedYear} disabled={!availability || status === 'loading'} onChange={(event) => {
              const selectedYear = Number(event.target.value);
              setYear(selectedYear);
              setMonth(outputReportMonths(availability, 'case-inflow', selectedYear).at(-1) ?? defaultMonthForYear(selectedYear));
              setAnalysis(null);
              setStatus('idle');
            }}>
              {!availableYears.length && <option value="">Caricamento…</option>}
              {availableYears.map((availableYear) => <option value={availableYear} key={availableYear}>{availableYear}</option>)}
            </select>
          </label>
          <label>
            <span>Mese</span>
            <select value={selectedMonth} disabled={!selectedYear || status === 'loading'} onChange={(event) => {
              setMonth(Number(event.target.value));
              setAnalysis(null);
              setStatus('idle');
            }}>
              {!month && <option value="">Seleziona</option>}
              {availableMonths.map((availableMonth) => (
                <option value={availableMonth} key={availableMonth}>{MONTHS[availableMonth - 1]}</option>
              ))}
            </select>
          </label>
          <button className="button button-primary" type="button" onClick={() => void loadAnalysis(analysis !== null)} disabled={!selectedYear || !selectedMonth || status === 'loading'}>
            {status === 'loading' ? <LoaderCircle className="spin" size={17} /> : <Inbox size={17} />}
            {status === 'loading' ? 'Elaborazione…' : analysis ? 'Rigenera report' : 'Genera report'}
          </button>
        </div>
      </section>

      {status === 'idle' && (
        <section className="trend-empty-state">
          <span><Inbox size={24} /></span>
          <div><strong>Il report è pronto per essere generato</strong><p>Scegli il periodo finale per combinare Master e ultimo file Mail disponibile.</p></div>
        </section>
      )}
      {status === 'loading' && (
        <section className="trend-empty-state loading">
          <span><LoaderCircle className="spin" size={24} /></span>
          <div><strong>Sto elaborando Master e Mail</strong><p>Calcolo entrate e chiusure mensili applicando il taglio al giorno 12.</p></div>
        </section>
      )}
      {status === 'error' && (
        <section className="trend-empty-state error">
          <span><AlertTriangle size={24} /></span>
          <div><strong>Report non disponibile</strong><p>{error}</p></div>
          <button type="button" className="button button-secondary" onClick={() => void loadAnalysis(false)}><RefreshCw size={16} /> Riprova</button>
        </section>
      )}

      {status === 'ready' && analysis && (
        <>
          <section className="trend-summary-grid" aria-label="Sintesi Case in entrata">
            <article><span>Periodi</span><strong>{analysis.rows.length}</strong><small>mesi consecutivi analizzati</small></article>
            <article><span>Case in entrata</span><strong>{formatNumber(analysis.totals.incoming)}</strong><small>tutti gli stati nel periodo mostrato</small></article>
            <article><span>Case chiusi</span><strong>{formatNumber(analysis.totals.closed)}</strong><small>totale nel periodo mostrato</small></article>
            <article><span>Saldo</span><strong>{formatNumber(analysis.totals.incoming - analysis.totals.closed)}</strong><small>entrata meno chiusi</small></article>
          </section>

          {analysis.generation && (
            <div className="report-generation-note" role="status">
              <Clock3 size={15} />
              <span>{formatReportGeneration(analysis.generation)}. Il pulsante “Rigenera report” sovrascrive questa versione.</span>
            </div>
          )}

          <ReportDataWarnings warnings={analysis.dataWarnings} />

          <section className="case-inflow-source-grid" aria-label="File sorgente">
            <div className="trend-file-bar">
              <div className="trend-file-icon"><FileSpreadsheet size={21} /></div>
              <div className="trend-file-copy">
                <p>Master annuali</p>
                <strong>{analysis.masterFiles.map(({ name }) => name).join(' · ')}</strong>
                <small>{requestedMaster ? `Aggiornato ${formatModifiedTime(requestedMaster.modifiedTime)}` : 'File annuali richiesti dal periodo'}</small>
              </div>
              {requestedMaster?.webViewLink && <a className="button button-secondary" href={requestedMaster.webViewLink} target="_blank" rel="noreferrer"><ExternalLink size={15} /> Apri</a>}
            </div>
            <div className="trend-file-bar">
              <div className="trend-file-icon mail"><Inbox size={21} /></div>
              <div className="trend-file-copy">
                <p>Ultimo file Mail {analysis.selectedYear}</p>
                <strong>{latestMail?.name ?? 'Non disponibile'}</strong>
                <small>{latestMail ? `${latestMail.path} · riferimento ${latestMail.referenceDate ?? 'non disponibile'}` : 'Nessun file associato'}</small>
              </div>
              {latestMail?.webViewLink && <a className="button button-secondary" href={latestMail.webViewLink} target="_blank" rel="noreferrer"><ExternalLink size={15} /> Apri</a>}
            </div>
          </section>

          <section className="trend-chart-card">
            <header>
              <div><p>Grafico</p><h2>Case in entrata e chiusi</h2><span>Confronto mensile sul periodo selezionato e sui dodici mesi precedenti.</span></div>
              <div className="trend-export-actions">
                <button type="button" className="button button-secondary" onClick={() => void exportPng()}><ImageDown size={15} /> PNG</button>
                <button type="button" className="button button-secondary" onClick={exportSvg}><FileDown size={15} /> SVG</button>
              </div>
            </header>
            <div className="trend-chart-scroll case-inflow-chart-scroll"><CaseInflowChart analysis={analysis} ref={svgRef} /></div>
          </section>

          <section className="trend-table-card case-inflow-table-card">
            <header>
              <div><p>Tabella</p><h2>Andamento annuale</h2><span>Entrata e chiusure, senza paginazione.</span></div>
              <button type="button" className="button button-primary" onClick={exportExcel}><Download size={15} /> Esporta Excel</button>
            </header>
            <div className="trend-table-scroll">
              <table className="trend-table case-inflow-table">
                <thead><tr><th>Andamento annuale</th><th><span>Entrata</span></th><th><span>Chiusi</span></th></tr></thead>
                <tbody>
                  {analysis.rows.map((row) => (
                    <tr key={`${row.year}-${row.month}`}>
                      <th scope="row">{row.label}</th>
                      <td>{formatNumber(row.incoming)}</td>
                      <td>{formatNumber(row.closed)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <footer><Table2 size={14} /> L’Excel mantiene valori numerici, filtri e intestazione bloccata.</footer>
          </section>

          {exportError && <div className="trend-export-error" role="alert"><AlertTriangle size={15} /> {exportError}</div>}
        </>
      )}
    </>
  );
}
